/**
 * useActividadesSearch — búsqueda en el catálogo F883 (`GET /api/actividades`).
 *
 * TALAPP-1921: extraído del `ActividadBuscador` de CompanyManager para que la
 * tarjeta de "Personas de la solicitud" sugiera actividades con el mismo
 * endpoint, el mismo debounce (300 ms) y el mismo mínimo (2 caracteres), sin
 * duplicar la llamada. La empresa guarda el CÓDIGO elegido; la persona guarda
 * el TEXTO (texto libre con sugerencias), así que el hook solo busca: qué se
 * hace con el resultado lo decide cada componente.
 */
import { useEffect, useState } from "react";

export interface ActividadEconomicaResult {
  id: number;
  codigo: string;
  descripcion: string;
  descripcionLarga?: string;
}

export const ACTIVIDADES_SEARCH_MIN_CHARS = 2;
export const ACTIVIDADES_SEARCH_DEBOUNCE_MS = 300;

export function useActividadesSearch(
  search: string,
  options: { enabled?: boolean; limit?: number } = {},
): { results: ActividadEconomicaResult[]; loading: boolean } {
  const { enabled = true, limit = 30 } = options;
  const [results, setResults] = useState<ActividadEconomicaResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const q = search.trim();
    if (q.length < ACTIVIDADES_SEARCH_MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/actividades?q=${encodeURIComponent(q)}&limit=${limit}`,
          { signal: controller.signal, credentials: "same-origin" }, // TALAPP-1859
        );
        const data = (await response.json()) as { success?: boolean; data?: ActividadEconomicaResult[] };
        if (data.success && Array.isArray(data.data)) setResults(data.data);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        // Sin red o sin sesión: la búsqueda simplemente no sugiere nada.
      } finally {
        setLoading(false);
      }
    }, ACTIVIDADES_SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [search, enabled, limit]);

  return { results, loading };
}
